import React from 'react';
import JQuery from 'jquery';
import { ISearchParams, NumberOrNull } from 'store/entity/types';
import { ResponseItems } from './styles';
import { display } from 'components/ErrorView/ErrorDialog';
import ResponseItem, { ITaskSurveyResponse } from './ResponseItem';
import Collapsible from '../../../../Collapsible';
import ResponseViewer from './ResponseViewer';
import { lightGreenSolid, white } from 'utils/style/colors';
import { IProviders, ITags } from 'store/providers/providerTypes';
import { IUser, IUsers } from 'store/users/usersType';
import {
  ISurveyItemWithData,
  ISurveyResponseView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ITasks, IThisUser } from 'store/store';
import { IAssignmentView } from 'store/tasks/tasksType';
import { ICompany } from 'store/companies/companiesTypes';
import i18n, { getCalendarLocal } from '../../../../../i18n';

const $: any = JQuery;

interface IResponseListProps {
  tags?: ITags['data'];
  users: IUsers['map'];
  item: ISurveyItemWithData;
  canEdit: boolean;
  canEditSensitive: boolean;
  responses: ISurveyView['responseMap'];
  // periodType?: TimeScale;
  survey: ISurveyView;
  thisUserId: number;
  selectedResponseId?: NumberOrNull;
  order: 'submitDate desc' | 'updateDate desc' | 'effectiveDate desc';
  onRemove: (responseId: number) => Promise<any>;
  templates: { id: number; title: string }[];
  expandable?: boolean;
  user?: IUser;
  surveyItem?: ISurveyItemWithData;
  createTask: (
    responseId: number,
    templateId: number,
    templateName: string,
    taskId?: number,
    taskSurveyResponses?: ITaskSurveyResponse
  ) => void;
  onUpdate: (response: ISurveyResponseView) => Promise<any>;
  updateAnalyse: () => void;
  onEditResponse: (responseId: number) => (e?: React.MouseEvent) => void;
  onClickOnResponse: (responseId: number | null) => void;
  searchTasks?: (params: ISearchParams) => Promise<any>;
  tasks?: ITasks;
  thisUser?: IThisUser;
  hasRelatedTemplate?: boolean;
  markTaskAssignment?: (assignment: IAssignmentView) => Promise<any>;
  handleShowTask?: (taskId: number) => any;
  company?: ICompany;
  providers?: IProviders;
  htmlUrl?: string;
  onSetHtmlUrl?: (newUrl: string) => void;
}

interface IResponseListState {
  openDeleteResponseId: number | undefined;
  lastSearchedResponseIds: string[];
}

class ResponseList extends React.Component<
  IResponseListProps,
  IResponseListState
> {
  constructor(props: IResponseListProps) {
    super(props);
    this.state = {
      openDeleteResponseId: undefined,
      lastSearchedResponseIds: [],
    };
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-string-refs
    $(this.refs.modal).modal({
      opacity: 0,
      dismissible: true,
    });

    if (
      this.props.company?.id &&
      this.props.searchTasks &&
      Object.values(this.props.responses || {})?.length > 0
    ) {
      this.handleSearchTasks();
    }
  }

  componentDidUpdate(prevProps: Readonly<IResponseListProps>) {
    if (!this.props.company?.id || !this.props.searchTasks) {
      return;
    }

    const currentResponseIds = Object.values(this.props.responses || {})
      .map((response) => response.id?.toString())
      .filter((it) => it !== undefined)
      .sort();

    const prevResponseIds = Object.values(prevProps.responses || {})
      .map((response) => response.id?.toString())
      .filter((it) => it !== undefined)
      .sort();

    const responseIdsChanged =
      currentResponseIds.length !== prevResponseIds.length ||
      currentResponseIds.some((id, index) => id !== prevResponseIds[index]);

    if (
      responseIdsChanged &&
      (Object.values(this.props.responses || {})?.length ?? 0) > 0
    )
      this.handleSearchTasks();
  }

  handleSearchTasks = () => {
    const currentResponseIds = Object.values(this.props.responses || {})
      .map((response) => response.id?.toString())
      .filter((it) => it !== undefined)
      .sort();

    const fetchedResponseIds = currentResponseIds
      ?.filter(
        (it) => !this.state.lastSearchedResponseIds?.includes(it as string)
      )
      .filter((it) => it !== undefined);

    if (fetchedResponseIds?.length > 0) {
      this.props.searchTasks?.({
        companyId: this.props.company!.id,
        fieldValuesMap: { responseId: fetchedResponseIds as string[] },
        archived: undefined,
        parentId: null,
        count: Math.max(5 * fetchedResponseIds?.length, 20),
      });
      this.setState({
        lastSearchedResponseIds: [
          ...((this.state.lastSearchedResponseIds || []) as string[]),
          ...(fetchedResponseIds as string[]),
        ],
      });
    }
  };

  handleClickOnResponse = (responseId: number) => () => {
    this.props.onClickOnResponse(responseId);
  };

  onRemove = (e: any) => {
    e.preventDefault();
    const { onRemove, updateAnalyse } = this.props;
    const target = e.currentTarget;
    const responseId = Number(target.dataset.responseid);
    const container = $(target.closest('li'));
    container.slideUp(500, () => {
      onRemove(responseId)
        .then(() => {
          updateAnalyse();
        })
        .catch((err: any) => {
          $(container).slideDown(500);
          display(err);
        });
    });
  };
  openDeleteModal = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      if (window.confirm(i18n.t('removeResponse', { ns: 'survey' }))) {
        this.onRemove(e);
      }
    }
  };

  onUpdateResponse = (responseId: number, state: 'lock' | 'draft') => {
    const { onUpdate, responses } = this.props;
    let response = responses![responseId];
    if (state === 'draft') {
      response = {
        ...response,
        draft: !response.draft,
        lock: false,
      };
    } else {
      response = {
        ...response,
        lock: !response.lock,
      };
    }
    return onUpdate(response);
  };

  render() {
    const {
      tags,
      users,
      canEdit,
      canEditSensitive,
      responses,
      survey,
      thisUserId,
      onEditResponse,
      selectedResponseId,
      item,
      templates,
      createTask,
      user,
      surveyItem,
      tasks,
      thisUser,
      hasRelatedTemplate,
      markTaskAssignment,
      handleShowTask,
      company,
      providers,
    } = this.props;

    const local = surveyItem?.data?.providerId
      ? providers?.map?.[surveyItem.data.providerId].data?.surveyOwnerProperties
          .localId || getCalendarLocal()
      : getCalendarLocal();

    return (
      <ResponseItems expandable={this.props.expandable}>
        {Object.values(responses!)
          .sort((a, b) => this.compare(b, a))
          .map((response) => {
            const responseId = response.id;
            const responseItem = (
              <>
                <ResponseItem
                  tags={tags}
                  users={users}
                  canEdit={canEdit}
                  canEditSensitive={canEditSensitive}
                  item={item}
                  response={response}
                  survey={survey}
                  key={response.id}
                  onClick={this.handleClickOnResponse}
                  responseId={responseId!}
                  thisUserId={thisUserId}
                  onEditResponse={onEditResponse}
                  openDeleteModal={this.openDeleteModal}
                  onUpdateResponse={this.onUpdateResponse}
                  templates={templates}
                  createTask={createTask}
                  embedded={this.props.expandable}
                  selectedItem={selectedResponseId}
                  local={local}
                />
              </>
            );

            if (this.props.expandable)
              return (
                <Collapsible
                  key={response.id || 'new'}
                  headerBackgroundColor={
                    selectedResponseId === responseId ? lightGreenSolid : white
                  }
                  customCollapsibleHeaderStyle={{ zIndex: 1000 }}
                  header={responseItem}
                  customHeaderStyle={{ flexGrow: 1, display: 'block' }}
                  customBodyStyle={{ padding: 0 }}
                  onOpen={() => this.props.onClickOnResponse(responseId!)}
                  onClose={() => this.props.onClickOnResponse(null)}
                  isOpen={selectedResponseId === responseId}
                >
                  <ResponseViewer
                    user={user!}
                    surveyItem={surveyItem!}
                    users={users}
                    canEdit={
                      !!selectedResponseId &&
                      (!!surveyItem!.data.editToken ||
                        (!!surveyItem!.data.changeDataToken &&
                          !survey.sensitiveChangeRequiresEditor) ||
                        !response?.lock ||
                        (!response?.lock && response?.userId === thisUserId))
                    }
                    onEditResponse={onEditResponse}
                    response={response}
                    tasks={tasks!}
                    thisUser={thisUser!}
                    companyId={company?.id}
                    hasRelatedTemplate={hasRelatedTemplate}
                    markTaskAssignment={markTaskAssignment!}
                    handleShowTask={handleShowTask!}
                    companyOwnerId={company?.owner?.userId}
                    survey={survey}
                    tags={tags!}
                    company={company}
                    embedded={true}
                    templates={templates}
                    onCreateTask={this.props.createTask}
                    htmlUrl={this.props?.htmlUrl}
                    onSetHtmlUrl={this.props?.onSetHtmlUrl}
                  />
                </Collapsible>
              );
            return responseItem;
          })}
      </ResponseItems>
    );
  }

  compare = (b: any, a: any) => {
    const [orderVar, orderSide] = this.props.order.split(' ');
    return b[orderVar] - a[orderVar] * (orderSide === 'desc' ? 1 : -1);
  };
}

export default ResponseList;
